import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useForm, Controller, SubmitHandler } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Stack,
  Typography,
  Grid,
  InputAdornment,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import { Cliente, ClienteRequest, Segmento } from '@/types/cliente'
import { maskCNPJ, maskCPF } from '@/utils/masks'
import { validateCNPJ, validateCPF } from '@/utils/validators'
import { clienteService } from '@/services/clienteService'
import { CurrencyField } from '@/components/common/CurrencyField'
import { useSnackbar } from 'notistack'
import { normalizarCnpj } from '@/utils/cnpj'

// Endereço é coletado para contato mas hoje o backend persiste apenas os campos
// essenciais (razaoSocial/cnpj/tipo/setor/faturamento). Por isso os campos de
// endereço são opcionais no schema.
const clienteFormSchema = z.object({
  razaoSocial: z
    .string()
    .min(3, 'Razão Social deve ter pelo menos 3 caracteres')
    .max(255, 'Razão Social deve ter no máximo 255 caracteres'),
  cnpj: z
    .string()
    .min(14, 'CNPJ inválido')
    .refine(
      (val) => validateCNPJ(val),
      'CNPJ inválido: confira os 14 caracteres (letras ou números + 2 dígitos verificadores)'
    ),
  cpf: z
    .string()
    .optional()
    .refine((val) => !val || validateCPF(val), 'CPF inválido'),
  tipo: z.enum(['PJ', 'PF']),
  segmento: z
    .string()
    .min(1, 'Segmento é obrigatório'),
  segmentoDescricao: z.string().optional().or(z.literal('')),
  faturamentoAnual: z
    .coerce.number({ invalid_type_error: 'Faturamento inválido' })
    .min(0, 'Faturamento não pode ser negativo'),
  cep: z.string().optional().or(z.literal('')),
  rua: z.string().optional().or(z.literal('')),
  numero: z.string().optional().or(z.literal('')),
  complemento: z.string().optional().or(z.literal('')),
  cidade: z.string().optional().or(z.literal('')),
  estado: z.string().optional().or(z.literal('')),
  observacoes: z.string().optional().or(z.literal('')),
})
  // Espelha a regra do backend: "Outros" sem descrição não diz nada. Validar aqui
  // é só conveniência — quem manda continua sendo o servidor.
  .refine(
    (data) => data.segmento !== 'OUTROS' || !!data.segmentoDescricao?.trim(),
    { message: 'Descreva o segmento', path: ['segmentoDescricao'] }
  )

type ClienteFormData = z.infer<typeof clienteFormSchema>

interface LocationState {
  cliente?: Cliente
}

const ClienteFormPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { cliente } = (location.state as LocationState) || {}
  const { enqueueSnackbar } = useSnackbar()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [segmentos, setSegmentos] = useState<Segmento[]>([])
  const [cepLoading, setCepLoading] = useState(false)

  const cnpjFromQuery = searchParams.get('cnpj') ?? ''
  // Quando o cadastro foi acionado a partir de /reservas/nova, voltamos para a
  // página de nova oportunidade (porta única, A07/A16) já com o CNPJ ao salvar/cancelar.
  const voltarParaReserva = searchParams.get('returnTo') === 'reserva'

  const destinoVoltar = useCallback(
    (cnpjDigitos?: string) => {
      if (!voltarParaReserva) return '/clientes'
      const cnpj = normalizarCnpj(cnpjDigitos ?? cnpjFromQuery)
      return `/reservas/nova${cnpj ? `?cnpj=${cnpj}` : ''}`
    },
    [voltarParaReserva, cnpjFromQuery]
  )

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ClienteFormData>({
    resolver: zodResolver(clienteFormSchema),
    defaultValues: cliente
      ? {
          razaoSocial: cliente.razaoSocial,
          cnpj: maskCNPJ(cliente.cnpj ?? ''),
          tipo: cliente.tipo,
          segmento: cliente.segmento ?? '',
          segmentoDescricao: cliente.segmentoDescricao ?? '',
          faturamentoAnual: cliente.faturamentoAnual,
          cep: '',
          rua: '',
          numero: '',
          complemento: '',
          cidade: '',
          estado: '',
          observacoes: '',
        }
      : {
          razaoSocial: '',
          cnpj: cnpjFromQuery ? maskCNPJ(cnpjFromQuery) : '',
          tipo: 'PJ',
          segmento: '',
          segmentoDescricao: '',
          faturamentoAnual: 0,
          cep: '',
          rua: '',
          numero: '',
          complemento: '',
          cidade: '',
          estado: '',
          observacoes: '',
        },
  })

  // Catálogo de segmentos: o backend é dono da lista. Se a chamada falhar o select
  // fica vazio — preferível a cair de volta numa cópia local que sai de sincronia.
  useEffect(() => {
    clienteService
      .listarSegmentos()
      .then(setSegmentos)
      .catch(() => setSegmentos([]))
  }, [])

  // Caso o CNPJ venha por query param depois do mount, reaplica no campo.
  useEffect(() => {
    if (!cliente && cnpjFromQuery) {
      setValue('cnpj', maskCNPJ(cnpjFromQuery))
    }
  }, [cliente, cnpjFromQuery, setValue])

  const tipoSelected = watch('tipo')
  const cepValue = watch('cep')
  const segmentoSelected = watch('segmento')

  /** Rótulo legível do segmento; '' enquanto o catálogo não chegou. */
  const rotuloSegmento = (valor: string) =>
    segmentos.find((s) => s.valor === valor)?.rotulo ?? ''

  const handleCepBlur = useCallback(async () => {
    const cep = (cepValue ?? '').replace(/\D/g, '')
    if (cep.length !== 8) return

    try {
      setCepLoading(true)
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      const data = await response.json()

      if (data.erro) {
        setError('CEP não encontrado')
        return
      }

      setValue('rua', data.logradouro)
      setValue('cidade', data.localidade)
      setValue('estado', data.uf)
      setError(null)
    } catch (err) {
      setError('Erro ao buscar CEP')
    } finally {
      setCepLoading(false)
    }
  }, [cepValue, setValue])

  const onSubmit: SubmitHandler<ClienteFormData> = async (data) => {
    try {
      setLoading(true)
      setError(null)

      // Endereço só é enviado quando todos os campos obrigatórios do DTO
      // (rua/numero/cidade/estado/cep) foram preenchidos — se faltar algum,
      // envia null para que o backend não aplique @NotBlank nos inners.
      const enderecoCompleto =
        data.rua && data.numero && data.cidade && data.estado && data.cep

      const clienteRequest: ClienteRequest = {
        razaoSocial: data.razaoSocial,
        cnpj: normalizarCnpj(data.cnpj),
        tipo: data.tipo,
        segmento: data.segmento,
        segmentoDescricao: data.segmento === 'OUTROS' ? data.segmentoDescricao : undefined,
        // `setorIndustria` é o campo de texto livre legado, ainda usado no grid e no
        // filtro por setor. Preenchido com o rótulo do segmento para esses continuarem
        // funcionando sem uma migração de dados que ninguém pediu.
        setorIndustria: rotuloSegmento(data.segmento) || data.segmento,
        faturamentoAnual: Number(data.faturamentoAnual) || 0,
        ...(enderecoCompleto
          ? {
              endereco: {
                rua: data.rua!,
                numero: data.numero!,
                complemento: data.complemento || undefined,
                cidade: data.cidade!,
                estado: data.estado!,
                cep: (data.cep || '').replace(/\D/g, ''),
              },
            }
          : {}),
      }

      if (cliente) {
        await clienteService.atualizar(cliente.id, {
          ...clienteRequest,
          status: cliente.status,
        })
        enqueueSnackbar('Cliente atualizado com sucesso', { variant: 'success' })
        navigate('/clientes')
      } else {
        await clienteService.criar(clienteRequest)
        enqueueSnackbar('Cliente cadastrado com sucesso', { variant: 'success' })
        // Volta para a Nova Reserva (com o CNPJ recém-cadastrado) quando o
        // cadastro veio desse fluxo; senão, segue para a lista de clientes.
        navigate(destinoVoltar(clienteRequest.cnpj))
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar cliente'
      setError(message)
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }


  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/clientes')}
          sx={{ cursor: 'pointer' }}
        >
          Clientes
        </Link>
        <Typography color="textPrimary">
          {cliente ? 'Editar Cliente' : 'Novo Cliente'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(destinoVoltar())}
        >
          Voltar
        </Button>
        <Typography variant="h4" fontWeight={600}>
          {cliente ? 'Editar Cliente' : 'Novo Cliente'}
        </Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Seção: Dados Principais */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Dados Principais" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="razaoSocial"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Razão Social"
                          fullWidth
                          size="small"
                          error={!!errors.razaoSocial}
                          helperText={errors.razaoSocial?.message}
                          required
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="cnpj"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="CNPJ"
                          fullWidth
                          size="small"
                          onChange={(e) => {
                            const masked = maskCNPJ(e.target.value)
                            field.onChange(masked)
                          }}
                          error={!!errors.cnpj}
                          helperText={errors.cnpj?.message}
                          placeholder="00.000.000/0000-00"
                          required
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="tipo"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Tipo</InputLabel>
                          <Select {...field} label="Tipo">
                            <MenuItem value="PJ">Pessoa Jurídica</MenuItem>
                            <MenuItem value="PF">Pessoa Física</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {tipoSelected === 'PF' && (
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="cpf"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="CPF"
                            fullWidth
                            size="small"
                            onChange={(e) => {
                              const masked = maskCPF(e.target.value)
                              field.onChange(masked)
                            }}
                            error={!!errors.cpf}
                            helperText={errors.cpf?.message}
                            placeholder="000.000.000-00"
                          />
                        )}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="segmento"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.segmento}>
                          {/* labelId/id explícitos: sem eles o MUI não associa o label
                              ao controle, e o campo fica sem nome para leitor de tela. */}
                          <InputLabel id="segmento-label">Segmento</InputLabel>
                          <Select
                            {...field}
                            labelId="segmento-label"
                            id="segmento"
                            label="Segmento"
                          >
                            {segmentos.map((s) => (
                              <MenuItem key={s.valor} value={s.valor}>
                                {s.rotulo}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.segmento && (
                            <FormHelperText>{errors.segmento.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {/* Só aparece em "Outros" — o backend recusa a opção sem descrição. */}
                  {segmentoSelected === 'OUTROS' && (
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="segmentoDescricao"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            required
                            label="Qual segmento?"
                            error={!!errors.segmentoDescricao}
                            helperText={errors.segmentoDescricao?.message}
                            placeholder="Ex.: Mineração"
                          />
                        )}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="faturamentoAnual"
                      control={control}
                      render={({ field }) => (
                        <CurrencyField
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          name={field.name}
                          inputRef={field.ref}
                          label="Faturamento Anual"
                          fullWidth
                          size="small"
                          error={!!errors.faturamentoAnual}
                          helperText={errors.faturamentoAnual?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Seção: Endereço */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Endereço" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Controller
                      name="cep"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="CEP"
                          fullWidth
                          size="small"
                          onChange={(e) => {
                            const masked = e.target.value.replace(/\D/g, '').slice(0, 8)
                            const formatted = masked.length === 8
                              ? `${masked.slice(0, 5)}-${masked.slice(5)}`
                              : masked
                            field.onChange(formatted)
                          }}
                          onBlur={handleCepBlur}
                          error={!!errors.cep}
                          helperText={errors.cep?.message}
                          placeholder="00000-000"
                          required
                          disabled={cepLoading}
                          InputProps={{
                            endAdornment: cepLoading && (
                              <InputAdornment position="end">
                                <CircularProgress size={20} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="rua"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Rua"
                          fullWidth
                          size="small"
                          error={!!errors.rua}
                          helperText={errors.rua?.message}
                          required
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Controller
                      name="numero"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Número"
                          fullWidth
                          size="small"
                          error={!!errors.numero}
                          helperText={errors.numero?.message}
                          required
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Controller
                      name="complemento"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Complemento"
                          fullWidth
                          size="small"
                          placeholder="Apto, Bloco, etc."
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="cidade"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Cidade"
                          fullWidth
                          size="small"
                          error={!!errors.cidade}
                          helperText={errors.cidade?.message}
                          required
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Controller
                      name="estado"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Estado"
                          fullWidth
                          size="small"
                          inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                          error={!!errors.estado}
                          helperText={errors.estado?.message}
                          required
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Seção: Observações */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Observações" />
              <CardContent>
                <Controller
                  name="observacoes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notas e Observações"
                      fullWidth
                      multiline
                      rows={4}
                      size="small"
                      placeholder="Adicione observações sobre o cliente..."
                    />
                  )}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Botões */}
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate(destinoVoltar())}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                type="submit"
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  )
}

export default ClienteFormPage
